import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Tile, TILE_TYPE_SUPER_FOLDER} from '../../shared/models/tile';
import {Option} from '../../shared/models/option';
import {SimpleStructure, SuperFolderStructure} from '../../shared/models/structure';
import {TilesService} from './tiles.service';
import {App} from '../../shared/models/app';
import {Hub} from '../../shared/models/hub';
import {Device} from '../../shared/models/device';
import {UserInvitation} from '../../shared/models/invitations';
import {Filters, FiltersConfig} from '../../shared/models/filter';
import {debounceTime} from 'rxjs/operators';
import {parseTileMetadata} from '../../shared/utils/metadata-parser.util';
import {Tag} from '../../shared/models/tag';
import {FilterGroup, FilterLeaf, FilterNode} from '../../shared/models/filter';

type SearchTermObject = RegExp | string;

const SEARCH_ENABLE_DEBOUNCE_DURATION = 150;

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  /**
   * Emits every time this.enableSearch() or this.disableSearch() is called
   */
  readonly searchEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _searchEnabledDebounce$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly searchResultQuantity$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /**
   * Controls whether metadata (point info like "Analog Input: 57") is included in search
   */
  readonly searchMetadataEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Emits every time this.enableFilters() or this.disableFilters() is called
   */
  readonly filtersEnabled$: BehaviorSubject<FiltersConfig> = new BehaviorSubject<FiltersConfig>(null);
  private _filtersEnabledDebounce$: BehaviorSubject<FiltersConfig> = new BehaviorSubject<FiltersConfig>(null);


  constructor(
    private tilesService: TilesService,
  ) {

    // Debounce is needed to prevent flickering of search box when switching
    // between two views that both have search enabled since search is auto-
    // matically disabled upon route changes (and potentially immediately
    // re-enabled).
    this._searchEnabledDebounce$.pipe(
      debounceTime(SEARCH_ENABLE_DEBOUNCE_DURATION),
    ).subscribe({
      next: (enabled) => {
        this.searchEnabled$.next(enabled);
      },
    });

    this._filtersEnabledDebounce$.pipe(
      debounceTime(SEARCH_ENABLE_DEBOUNCE_DURATION),
    ).subscribe({
      next: (config) => {
        this.filtersEnabled$.next(config);
      },
    });
  }


  enableSearch(): void {
    this._searchEnabledDebounce$.next(true);
  }


  disableSearch(): void {
    this._searchEnabledDebounce$.next(false);
  }


  enableFilters(config: FiltersConfig): void {
    this._filtersEnabledDebounce$.next(config);
  }


  disableFilters(): void {
    this._filtersEnabledDebounce$.next(null);
  }


  setResultQty(quantity: number): void {
    this.searchResultQuantity$.next(quantity);
  }


  setMetadataSearchEnabled(enabled: boolean): void {
    this.searchMetadataEnabled$.next(enabled);
  }


  isMetadataSearchEnabled(): boolean {
    return this.searchMetadataEnabled$.value;
  }


  searchInvitations(searchTerm: string, invitesToSearch: UserInvitation[]): UserInvitation[] {
    if (!searchTerm) {
      return invitesToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);
    const results = invitesToSearch.filter((i) => {
      return this.searchRegExGeneric(sto, i.InviteeName) ||
        this.searchRegExGeneric(sto, i.InviteeEmail);
    });

    return results;
  }


  searchApps(searchTerm: string, appsToSearch: App[]): App[] {
    if (!searchTerm) {
      return appsToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);
    const results = appsToSearch.filter((a) => {
      const subtitle = a.Config?.Subtitle?.Text || '';
      return this.searchRegExGeneric(sto, a.Name, '' + a.ID) ||
             this.searchRegExGeneric(sto, subtitle);
    });

    return results;
  }


  searchHubs(searchTerm: string, hubsToSearch: Hub[]): Hub[] {
    if (!searchTerm) {
      return hubsToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);
    const results = hubsToSearch.filter((h) => {
      return this.searchRegExGeneric(sto, h.Name, '' + h.ID);
    });

    return results;
  }


  searchDevices(searchTerm: string, devicesToSearch: Device[]): Device[] {
    if (!searchTerm) {
      return devicesToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);
    const results = devicesToSearch.filter((d) => {
      return this.searchRegExGeneric(sto, d.Name, '' + d.ID) ||
             this.searchRegExGeneric(sto, d.Address);
    });

    return results;
  }


  /**
   *
   * @param searchTerm
   * @param optionsToSearch
   */
  searchOptions<T extends Option>(searchTerm: string, optionsToSearch: T[]): T[] {
    if (!searchTerm) {
      return optionsToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);

    const results = optionsToSearch.filter((opt) => {
      // Search by name (partial match) and ID (exact match)
      if (this.searchRegExGeneric(sto, opt.name, opt.id)) {
        return true;
      }

      // Also search pill text (partial match) for tile IDs or other pill content
      if (opt.pill?.text && this.searchRegExGeneric(sto, String(opt.pill.text))) {
        return true;
      }

      return false;
    });

    return results;
  }


  /**
   *
   * @param searchTerm
   * @param tilesToSearch - The complete set of tiles to which to apply the
   *  search for searchTerm
   */
  searchTiles(searchTerm: string, tilesToSearch: Tile[]): Tile[] {
    if (!searchTerm) {
      return tilesToSearch;
    }

    const sto = this.prepareSearchTerm(searchTerm);
    const results = tilesToSearch.filter((tile) => {
      return this._searchTile(sto, tile);
    });

    return results;
  }


  tileMatchesSelf(searchTerm: string, tile: Tile): boolean {
    if (!searchTerm) {
      return true;
    }

    return this._tileSelfMatches(this.prepareSearchTerm(searchTerm), tile);
  }


  filterTiles(filters: Filters, tilesToSearch: Tile[]): Tile[] {
    return tilesToSearch.filter((tile) => {
      if (filters.devices?.length) {
        const deviceIds = tile.getDeviceIds();

        if (!deviceIds) {
          return false;
        }

        for (const deviceId of deviceIds) {
          if (!filters.devices.includes(deviceId)) {
            return false;
          }
        }
      }

      if (filters.owners?.length) {
        if (!filters.owners.includes(tile.Owner)) {
          return false;
        }
      }

      if (filters.tagFilters?.Filters?.length) {
        const appliedTags = tile.Tags ?? [];
        for (const node of filters.tagFilters.Filters) {
          if (!this.matchesFilterNode(node, appliedTags)) {
            return false;
          }
        }
      }

      return true;
    });
  }

  private matchesFilterNode(node: FilterNode, appliedTags: Tag[]): boolean {
    if ('Key' in node) {
      const leaf = node as FilterLeaf;
      if (leaf.Key !== 'tag') return true;
      return appliedTags.some(at => {
        const idMatch = leaf.ID ? at.ID === leaf.ID : at.Label.toLowerCase() === leaf.Label?.toLowerCase();
        const valueMatch = leaf.Value
          ? at.Value?.toLowerCase() === leaf.Value.toLowerCase()
          : true;
        return idMatch && valueMatch;
      });
    }
    const group = node as FilterGroup;
    const recurse = (child: FilterNode) => this.matchesFilterNode(child, appliedTags);
    return group.Operator === 'or'
      ? group.Filters.some(recurse)
      : group.Filters.every(recurse);
  }


  filterDevices(filters: Filters, devicesToSearch: Device[]): Device[] {
    return devicesToSearch.filter((device) => {
      if (filters.hubs?.length) {
        if (!filters.hubs.includes(device.Hub)) {
          return false;
        }
      }

      if (filters.owners?.length) {
        if (!filters.owners.includes(device.Owner)) {
          return false;
        }
      }

      return true;
    });
  }


  /**
   *
   * @param searchTermObj - The search term against which to qualify the tile
   * @param tile - The tile to qualify against searchTerm
   * @param depth - Because this is a recursive function
   * @private
   */
  private _searchTile(searchTermObj: SearchTermObject, tile: Tile, depth = 0): boolean {
    if (++depth > 10) {
      throw new Error('recursion in _searchTile too deep');
    }

    if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
      const childIds = (tile.Structure as SuperFolderStructure).TileIDs;

      for (const childId of childIds) {
        const childTile = this.tilesService.sessUserTiles[childId];

        if (childTile) {
          const result = this._searchTile(searchTermObj, childTile, depth);
          if (result) {
            return true;
          }
        }
      }
    }

    return this._tileSelfMatches(searchTermObj, tile);
  }


  private _tileSelfMatches(searchTermObj: SearchTermObject, tile: Tile): boolean {
    const tileNameToSearch = `/^(${tile.Appearance.DisplayName}|${tile.Name})$/`;

    if (this.searchRegExGeneric(searchTermObj, tileNameToSearch, '' + tile.ID)) {
      return true;
    }

    if (this.isMetadataSearchEnabled()) {
      const metadata = this._extractTileMetadata(tile);
      if (metadata && this.searchRegExGeneric(searchTermObj, metadata)) {
        return true;
      }
    }

    return false;
  }


  /**
   * Extracts searchable metadata from a tile's structure
   * Handles BACnet, Modbus, and LoRaWAN protocols
   * @param tile - The tile to extract metadata from
   * @returns A string containing searchable metadata fields
   * @private
   */
  private _extractTileMetadata(tile: Tile): string | null {
    const structure = tile.Structure as SimpleStructure;
    const parsed = parseTileMetadata(structure);
    return parsed?.searchableText || null;
  }


  /**
   * Based on the supplied search term string, returns either the same
   * string or a RegExp instance that can be fed to searchRegExGeneric.
   *
   * If the supplied term starts and ends with '/' then the returned
   * value will be a RegExp; otherwise a string.
   *
   * @param searchTerm
   * @private
   */
  private prepareSearchTerm(searchTerm: string): SearchTermObject {
    searchTerm = searchTerm.toLowerCase();

    const isRegEx: boolean = searchTerm[0] === '/' && searchTerm[searchTerm.length - 1] === '/';

    if (isRegEx) {
      // strip beginning and end slash
      searchTerm = searchTerm.substring(1, searchTerm.length - 1);

      try {
        return new RegExp(searchTerm, 'i');
      } catch (e) {

      }
    }

    return searchTerm;
  }


  /**
   *
   * @param searchTermObj
   * @param partialMatch
   * @param exactMatch
   * @private
   */
  private searchRegExGeneric(
    searchTermObj: SearchTermObject,
    partialMatch: string,
    exactMatch?: string,
  ): boolean {
    if (searchTermObj instanceof RegExp) {
      return searchTermObj.test(partialMatch)
        || (exactMatch === searchTermObj.source);
    }

    return partialMatch.toLowerCase().indexOf(searchTermObj) > -1
      || (exactMatch === searchTermObj);
  }
}
